/**
 * <Blink> — ce qu'il porte clignote, tant qu'il est monté (atom, `proposé`).
 *
 * Une vraie alerte, et rien d'autre : la chip d'un élève du live sorti de l'appli ou silencieux depuis 10 min
 * (daily du 04/11, le rouge clignotant de l'ancienne pastille, `LiveParticipantIndicator`). Le contenu passe
 * de plein à `alertBlink.dim` et revient, au rythme de l'ancienne (`tokens/motion` → `alertBlink`).
 *
 * Comme `<LiveDot>` : au natif, le pilote natif ; au web, une animation CSS que le navigateur joue seul (la
 * boucle d'`Animated` y coûte du script à chaque image, à côté de la vidéo du live). En mouvement réduit, il
 * ne clignote pas : le contenu reste plein, sa couleur dit l'alerte.
 */
import React, { useEffect, useRef } from "react"
import { Animated, Platform, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { alertBlink } from "../../theme/tokens/motion"
import { injectStyleOnce } from "../../utils/injectStyleOnce"

const WEB_BLINK = "masteur-alert-blink"
const WEB_BLINK_CSS = `@keyframes ${WEB_BLINK} {
  0%, 100% { opacity: 1; }
  50% { opacity: ${alertBlink.dim}; }
}`

export type BlinkProps = {
    children: React.ReactNode
    testID?: string
}

export function Blink(props: BlinkProps) {
    const { children, testID } = props
    const reduceMotion = useReduceMotion()
    const web = Platform.OS === "web"
    const opacity = useRef(new Animated.Value(1)).current

    useEffect(() => {
        opacity.setValue(1)
        if (reduceMotion) return undefined
        if (web) {
            injectStyleOnce(WEB_BLINK, WEB_BLINK_CSS)
            return undefined
        }
        const step = (toValue: number) =>
            Animated.timing(opacity, { toValue, duration: alertBlink.half, useNativeDriver: true })
        const loop = Animated.loop(Animated.sequence([step(alertBlink.dim), step(1)]))
        loop.start()
        return () => loop.stop()
    }, [reduceMotion, web, opacity])

    if (reduceMotion) return <View testID={testID}>{children}</View>
    if (web)
        return (
            <View style={styles.webBlink} testID={testID}>
                {children}
            </View>
        )
    return (
        <View testID={testID}>
            <Animated.View style={{ opacity }}>{children}</Animated.View>
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    webBlink: {
        _web: {
            animationName: WEB_BLINK,
            animationDuration: `${alertBlink.half * 2}ms`,
            animationIterationCount: "infinite",
            animationTimingFunction: "ease-in-out",
        },
    },
}))
